import React from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useColorModeValue,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import campaignApi from "../api/campaignApi";
import ModulePageShell from "../components/common/ModulePageShell";
import LoadingState from "../components/common/LoadingState";
import EmptyState from "../components/common/EmptyState";
import { PERFORMANCE_QUERY_KEYS } from "../constants/performanceConstants";
import usePerformancePermissions from "../hooks/usePerformancePermissions";
import usePerformanceUsers from "../hooks/usePerformanceUsers";

const CampaignDetailPage = () => {
  const { campaignId } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can, PERFORMANCE_PERMISSIONS } = usePerformancePermissions();
  const { userOptions, isLoading: usersLoading } = usePerformanceUsers();
  const [showGuide, setShowGuide] = React.useState(false);
  const [participantDraft, setParticipantDraft] = React.useState({
    employeeId: "",
    managerId: "",
    reviewerIds: [],
  });
  const [participantSearch, setParticipantSearch] = React.useState({
    employee: "",
    manager: "",
    reviewer: "",
  });
  const [editableParticipants, setEditableParticipants] = React.useState({});
  const [activeRowAction, setActiveRowAction] = React.useState(null);
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const softBg = useColorModeValue("gray.50", "gray.700");
  const mutedText = useColorModeValue("gray.600", "gray.300");

  const canRead360 = can(PERFORMANCE_PERMISSIONS.APPRAISAL_360_READ);
  const canManage360 = can(PERFORMANCE_PERMISSIONS.APPRAISAL_360_MANAGE);

  if (!canRead360) {
    return (
      <ModulePageShell
        title="Campaign Detail"
        description="Overview, participants, reviews, and analytics for a specific campaign."
      >
        <EmptyState message="You do not have permission to view campaign details." />
      </ModulePageShell>
    );
  }

  const campaignQuery = useQuery({
    queryKey: [PERFORMANCE_QUERY_KEYS.campaignDetail, campaignId],
    queryFn: () => campaignApi.getCampaignById(campaignId),
    enabled: Boolean(campaignId),
  });

  const campaign = campaignQuery.data?.data;
  const participants = Array.isArray(campaign?.participants)
    ? campaign.participants
    : [];

  const toObjectId = (value) =>
    typeof value === "string" ? value : value?._id || "";
  const toObjectIdArray = (values = []) =>
    Array.isArray(values)
      ? values.map((item) => toObjectId(item)).filter(Boolean)
      : [];

  const getUserLabelById = (value) => {
    const userId = toObjectId(value);
    if (!userId) return "-";

    const option = userOptions.find((item) => item.value === userId);
    return option?.label || userId;
  };

  const normalize = (value) => String(value || "").toLowerCase();

  const filterOptions = (options, searchValue) => {
    if (!searchValue) return options;
    const term = normalize(searchValue);
    return options.filter((option) => normalize(option.label).includes(term));
  };

  const filteredEmployeeOptions = filterOptions(
    userOptions,
    participantSearch.employee,
  );
  const filteredManagerOptions = filterOptions(
    userOptions,
    participantSearch.manager,
  );
  const filteredReviewerOptions = filterOptions(
    userOptions,
    participantSearch.reviewer,
  );

  const buildParticipantPayload = (list) =>
    list.map((item) => ({
      employeeId: toObjectId(item.employeeId),
      managerId: toObjectId(item.managerId),
      reviewers: toObjectIdArray(item.reviewers),
      selfReviewCompleted: Boolean(item.selfReviewCompleted),
      managerReviewCompleted: Boolean(item.managerReviewCompleted),
      overallStatus: item.overallStatus || "PENDING",
    }));

  const updateParticipantsMutation = useMutation({
    mutationFn: ({ nextParticipants }) =>
      campaignApi.updateCampaign(campaignId, {
        participants: nextParticipants,
      }),
    onMutate: ({ rowKey, actionType }) => {
      setActiveRowAction({ rowKey, actionType });
    },
    onSuccess: () => {
      toast({
        title: "Participants updated",
        status: "success",
        duration: 2000,
      });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.campaignDetail, campaignId],
      });
      queryClient.invalidateQueries({
        queryKey: [PERFORMANCE_QUERY_KEYS.campaigns],
      });
      setParticipantDraft({ employeeId: "", managerId: "", reviewerIds: [] });
      setParticipantSearch({ employee: "", manager: "", reviewer: "" });
    },
    onError: (error) => {
      toast({
        title: "Failed to update participants",
        description: error?.response?.data?.message || error.message,
        status: "error",
      });
    },
    onSettled: () => {
      setActiveRowAction(null);
    },
  });

  const startEditParticipant = (participant) => {
    const employeeId = toObjectId(participant.employeeId);

    setEditableParticipants((prev) => ({
      ...prev,
      [employeeId]: {
        managerId: toObjectId(participant.managerId),
        reviewerIds: toObjectIdArray(participant.reviewers),
      },
    }));
  };

  const cancelEditParticipant = (employeeId) => {
    setEditableParticipants((prev) => {
      const next = { ...prev };
      delete next[employeeId];
      return next;
    });
  };

  const saveEditParticipant = (employeeId) => {
    const draft = editableParticipants[employeeId];
    if (!draft?.managerId) {
      toast({
        title: "Manager is required",
        status: "warning",
        duration: 2000,
      });
      return;
    }

    const nextParticipants = buildParticipantPayload(
      participants.map((participant) => {
        const currentEmployeeId = toObjectId(participant.employeeId);
        if (currentEmployeeId !== employeeId) return participant;

        return {
          ...participant,
          managerId: draft.managerId,
          reviewers: draft.reviewerIds,
        };
      }),
    );

    updateParticipantsMutation.mutate({
      nextParticipants,
      rowKey: employeeId,
      actionType: "save",
    });

    cancelEditParticipant(employeeId);
  };

  const handleAddParticipant = () => {
    if (!participantDraft.employeeId || !participantDraft.managerId) {
      toast({
        title: "Employee and manager are required",
        status: "warning",
        duration: 2000,
      });
      return;
    }

    const alreadyAdded = participants.some(
      (item) => toObjectId(item.employeeId) === participantDraft.employeeId,
    );

    if (alreadyAdded) {
      toast({
        title: "Participant already exists",
        status: "warning",
        duration: 2000,
      });
      return;
    }

    const nextParticipants = [
      ...buildParticipantPayload(participants),
      {
        employeeId: participantDraft.employeeId,
        managerId: participantDraft.managerId,
        reviewers: participantDraft.reviewerIds,
        selfReviewCompleted: false,
        managerReviewCompleted: false,
        overallStatus: "PENDING",
      },
    ];

    updateParticipantsMutation.mutate({
      nextParticipants,
      rowKey: "add-participant",
      actionType: "add",
    });
  };

  const handleRemoveParticipant = (employeeId) => {
    const nextParticipants = buildParticipantPayload(
      participants.filter((item) => toObjectId(item.employeeId) !== employeeId),
    );

    updateParticipantsMutation.mutate({
      nextParticipants,
      rowKey: employeeId,
      actionType: "remove",
    });
  };

  return (
    <ModulePageShell
      title="Campaign Detail"
      description="Overview, participants, reviews, and analytics for a specific campaign."
      onRefresh={() => campaignQuery.refetch()}
      showGuide={showGuide}
      onToggleGuide={() => setShowGuide((prev) => !prev)}
    >
      {campaignQuery.isLoading ? (
        <LoadingState />
      ) : !campaign ? (
        <EmptyState message="Campaign not found." />
      ) : (
        <Box
          bg={bg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={{ base: 3, md: 5 }}
        >
          <Tabs variant="soft-rounded" colorScheme="blue">
            <TabList>
              <Tab>Overview</Tab>
              <Tab>Participants</Tab>
              <Tab>Reviews</Tab>
              <Tab>Analytics</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <VStack align="stretch" spacing={4}>
                  <HStack justify="space-between" align="center" wrap="wrap">
                    <Text fontSize="xl" fontWeight="bold">
                      {campaign.name}
                    </Text>
                    <Badge
                      colorScheme={
                        campaign.status === "ACTIVE"
                          ? "green"
                          : campaign.status === "CLOSED"
                            ? "purple"
                            : "yellow"
                      }
                      px={3}
                      py={1}
                      borderRadius="full"
                    >
                      {campaign.status || "DRAFT"}
                    </Badge>
                  </HStack>

                  <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
                    <Box bg={softBg} borderRadius="md" p={3}>
                      <Text
                        fontSize="xs"
                        color={mutedText}
                        textTransform="uppercase"
                      >
                        Campaign Code
                      </Text>
                      <Text fontWeight="semibold">{campaign.code || "-"}</Text>
                    </Box>
                    <Box bg={softBg} borderRadius="md" p={3}>
                      <Text
                        fontSize="xs"
                        color={mutedText}
                        textTransform="uppercase"
                      >
                        Goal Cycle
                      </Text>
                      <Text fontWeight="semibold">
                        {campaign.goalCycleId?.name ||
                          campaign.goalCycleId ||
                          "-"}
                      </Text>
                    </Box>
                    <Box bg={softBg} borderRadius="md" p={3}>
                      <Text
                        fontSize="xs"
                        color={mutedText}
                        textTransform="uppercase"
                      >
                        Start Date
                      </Text>
                      <Text fontWeight="semibold">
                        {campaign.startDate
                          ? new Date(campaign.startDate).toLocaleDateString()
                          : "-"}
                      </Text>
                    </Box>
                    <Box bg={softBg} borderRadius="md" p={3}>
                      <Text
                        fontSize="xs"
                        color={mutedText}
                        textTransform="uppercase"
                      >
                        End Date
                      </Text>
                      <Text fontWeight="semibold">
                        {campaign.endDate
                          ? new Date(campaign.endDate).toLocaleDateString()
                          : "-"}
                      </Text>
                    </Box>
                  </SimpleGrid>

                  <Text fontSize="sm" color={mutedText}>
                    Participants configured: {participants.length}
                  </Text>
                </VStack>
              </TabPanel>
              <TabPanel>
                <VStack align="stretch" spacing={4}>
                  <HStack justify="space-between" align="center" wrap="wrap">
                    <Text fontWeight="semibold">Participants</Text>
                    <Badge colorScheme="blue" borderRadius="full" px={3} py={1}>
                      {participants.length}
                    </Badge>
                  </HStack>

                  {canManage360 ? (
                    <Box
                      borderWidth="1px"
                      borderColor={borderColor}
                      borderRadius="md"
                      p={{ base: 3, md: 4 }}
                      bg={softBg}
                    >
                      <VStack align="stretch" spacing={3}>
                        <Text fontWeight="medium">Add Participant</Text>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                          <FormControl isRequired>
                            <FormLabel>Employee</FormLabel>
                            <Input
                              size="sm"
                              mb={2}
                              placeholder="Search employee"
                              value={participantSearch.employee}
                              onChange={(event) =>
                                setParticipantSearch((prev) => ({
                                  ...prev,
                                  employee: event.target.value,
                                }))
                              }
                            />
                            <Select
                              placeholder="Select employee"
                              value={participantDraft.employeeId}
                              isDisabled={usersLoading}
                              onChange={(event) =>
                                setParticipantDraft((prev) => ({
                                  ...prev,
                                  employeeId: event.target.value,
                                }))
                              }
                            >
                              {filteredEmployeeOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl isRequired>
                            <FormLabel>Manager</FormLabel>
                            <Input
                              size="sm"
                              mb={2}
                              placeholder="Search manager"
                              value={participantSearch.manager}
                              onChange={(event) =>
                                setParticipantSearch((prev) => ({
                                  ...prev,
                                  manager: event.target.value,
                                }))
                              }
                            />
                            <Select
                              placeholder="Select manager"
                              value={participantDraft.managerId}
                              isDisabled={usersLoading}
                              onChange={(event) =>
                                setParticipantDraft((prev) => ({
                                  ...prev,
                                  managerId: event.target.value,
                                }))
                              }
                            >
                              {filteredManagerOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                          </FormControl>
                        </SimpleGrid>

                        <Divider />

                        <FormControl>
                          <FormLabel>
                            Reviewers (optional, multi-select)
                          </FormLabel>
                          <Input
                            size="sm"
                            mb={2}
                            placeholder="Search reviewers"
                            value={participantSearch.reviewer}
                            onChange={(event) =>
                              setParticipantSearch((prev) => ({
                                ...prev,
                                reviewer: event.target.value,
                              }))
                            }
                          />
                          <CheckboxGroup
                            colorScheme="blue"
                            value={participantDraft.reviewerIds}
                            isDisabled={usersLoading}
                            onChange={(selectedValues) => {
                              setParticipantDraft((prev) => ({
                                ...prev,
                                reviewerIds: selectedValues,
                              }));
                            }}
                          >
                            <Stack
                              spacing={2}
                              maxH="150px"
                              overflowY="auto"
                              borderWidth="1px"
                              borderColor={borderColor}
                              borderRadius="md"
                              p={3}
                              bg={bg}
                            >
                              {filteredReviewerOptions.map((option) => (
                                <Checkbox
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </Checkbox>
                              ))}
                            </Stack>
                          </CheckboxGroup>
                          <Text mt={2} fontSize="xs" color={mutedText}>
                            Tip: choose peers/subordinates who should review
                            this participant.
                          </Text>
                        </FormControl>

                        <Button
                          alignSelf="flex-start"
                          colorScheme="blue"
                          onClick={handleAddParticipant}
                          isLoading={
                            updateParticipantsMutation.isPending &&
                            activeRowAction?.rowKey === "add-participant"
                          }
                          isDisabled={
                            !participantDraft.employeeId ||
                            !participantDraft.managerId ||
                            usersLoading
                          }
                        >
                          Add Participant
                        </Button>
                      </VStack>
                    </Box>
                  ) : null}

                  {participants.length === 0 ? (
                    <EmptyState message="No participants added yet." />
                  ) : (
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>Employee</Th>
                          <Th>Manager</Th>
                          <Th>Reviewers</Th>
                          <Th>Status</Th>
                          {canManage360 ? <Th>Action</Th> : null}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {participants.map((participant) => {
                          const employeeId = toObjectId(participant.employeeId);
                          const reviewerIds = toObjectIdArray(
                            participant.reviewers,
                          );
                          const isEditing = Boolean(
                            editableParticipants[employeeId],
                          );
                          const editDraft = editableParticipants[employeeId];

                          return (
                            <Tr key={employeeId}>
                              <Td>
                                {getUserLabelById(participant.employeeId)}
                              </Td>
                              <Td>
                                {isEditing ? (
                                  <Select
                                    size="sm"
                                    value={editDraft.managerId}
                                    onChange={(event) =>
                                      setEditableParticipants((prev) => ({
                                        ...prev,
                                        [employeeId]: {
                                          ...prev[employeeId],
                                          managerId: event.target.value,
                                        },
                                      }))
                                    }
                                  >
                                    {userOptions.map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </Select>
                                ) : (
                                  getUserLabelById(participant.managerId)
                                )}
                              </Td>
                              <Td>
                                {isEditing ? (
                                  <CheckboxGroup
                                    value={editDraft.reviewerIds}
                                    onChange={(selectedValues) =>
                                      setEditableParticipants((prev) => ({
                                        ...prev,
                                        [employeeId]: {
                                          ...prev[employeeId],
                                          reviewerIds: selectedValues,
                                        },
                                      }))
                                    }
                                  >
                                    <Stack
                                      spacing={1}
                                      maxH="100px"
                                      overflowY="auto"
                                      borderWidth="1px"
                                      borderColor={borderColor}
                                      borderRadius="md"
                                      p={2}
                                    >
                                      {userOptions.map((option) => (
                                        <Checkbox
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </Checkbox>
                                      ))}
                                    </Stack>
                                  </CheckboxGroup>
                                ) : reviewerIds.length ? (
                                  reviewerIds
                                    .map((reviewerId) =>
                                      getUserLabelById(reviewerId),
                                    )
                                    .join(", ")
                                ) : (
                                  "-"
                                )}
                              </Td>
                              <Td>{participant.overallStatus || "PENDING"}</Td>
                              {canManage360 ? (
                                <Td>
                                  <HStack spacing={2}>
                                    {isEditing ? (
                                      <>
                                        <Button
                                          size="xs"
                                          colorScheme="blue"
                                          onClick={() =>
                                            saveEditParticipant(employeeId)
                                          }
                                          isLoading={
                                            updateParticipantsMutation.isPending &&
                                            activeRowAction?.rowKey ===
                                              employeeId &&
                                            activeRowAction?.actionType ===
                                              "save"
                                          }
                                        >
                                          Save
                                        </Button>
                                        <Button
                                          size="xs"
                                          variant="outline"
                                          onClick={() =>
                                            cancelEditParticipant(employeeId)
                                          }
                                        >
                                          Cancel
                                        </Button>
                                      </>
                                    ) : (
                                      <Button
                                        size="xs"
                                        variant="outline"
                                        onClick={() =>
                                          startEditParticipant(participant)
                                        }
                                      >
                                        Edit
                                      </Button>
                                    )}

                                    <Button
                                      size="xs"
                                      colorScheme="red"
                                      variant="outline"
                                      onClick={() =>
                                        handleRemoveParticipant(employeeId)
                                      }
                                      isLoading={
                                        updateParticipantsMutation.isPending &&
                                        activeRowAction?.rowKey ===
                                          employeeId &&
                                        activeRowAction?.actionType === "remove"
                                      }
                                    >
                                      Remove
                                    </Button>
                                  </HStack>
                                </Td>
                              ) : null}
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  )}
                </VStack>
              </TabPanel>
              <TabPanel>
                <Text>
                  Use the 360 Reviews page to submit and track campaign reviews.
                </Text>
              </TabPanel>
              <TabPanel>
                <Text>
                  Campaign analytics is available in the Performance Analytics
                  page.
                </Text>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      )}
    </ModulePageShell>
  );
};

export default CampaignDetailPage;
